// @ts-check
import React, { useMemo, useState } from "react"
import {
  Accordion,
  AccordionTab,
} from "../../../../../components/atoms/Accordion.atom"
import { Timeline } from "../../../../../components/atoms/Timeline.atom"
import { clsx } from "../../../../../utils/strings.utils"
import {
  STREAM_ACTIVITY_KIND_TOOL,
} from "./streamActivityReducer"
import {
  STREAM_ACTIVITY_TIMELINE_EVENT,
  mapStreamTimelineEventToIconClass,
  streamTimelineEventFromSegment,
} from "./streamActivityTimelineIcons"

const READY_LABEL = "Listo"

/**
 * @typedef {import("./estimationAgent.types").StreamActivitySegment} StreamActivitySegment
 */

/**
 * @param {StreamActivitySegment[]} segments
 * @param {boolean} streaming
 * @returns {{ id: string; label: string; timelineEvent: string }[]}
 */
function buildTimelineItems(segments, streaming) {
  const completed = segments.filter((s) => s.status === "completed")
  const active = segments.filter((s) => s.status === "active")
  const rows = [
    ...completed.map((c) => ({
      id: c.id,
      label: c.label,
      timelineEvent: streamTimelineEventFromSegment(c) ?? "",
    })),
    ...active.map((a) => ({
      id: a.id,
      label: a.label,
      timelineEvent: streamTimelineEventFromSegment(a) ?? "",
    })),
  ]
  const last = segments[segments.length - 1]
  const holdReadyAfterCalculate =
    streaming &&
    active.length === 0 &&
    last &&
    last.kind === STREAM_ACTIVITY_KIND_TOOL &&
    last.toolId === "calculate"
  if (active.length === 0 && !holdReadyAfterCalculate) {
    rows.push({
      id: "__ready",
      label: READY_LABEL,
      timelineEvent: STREAM_ACTIVITY_TIMELINE_EVENT.READY,
    })
  }
  return rows
}

/**
 * Hueco entre dos llamadas `calculate`: todo queda "completed" y antes se mostraba
 * "Listo" hasta la siguiente tool. Mientras el stream sigue, mantenemos la etiqueta
 * del último `calculate` completado.
 *
 * @param {StreamActivitySegment[]} segments
 * @param {boolean} streaming
 * @returns {string}
 */
function accordionHeaderLabel(segments, streaming) {
  const active = segments.filter((s) => s.status === "active")
  if (active.length > 0) {
    return active[active.length - 1].label
  }
  if (streaming && segments.length > 0) {
    const last = segments[segments.length - 1]
    if (
      last.kind === STREAM_ACTIVITY_KIND_TOOL &&
      last.toolId === "calculate"
    ) {
      return last.label
    }
  }
  return READY_LABEL
}

/**
 * @param {{ item: { id: string; label: string; timelineEvent: string } }} props
 */
function TimelineRowContent({ item }) {
  const iconClass = mapStreamTimelineEventToIconClass(item.timelineEvent)
  return (
    <span
      className={clsx(
        "estimation-agent-quick-v3__timeline-label",
        iconClass && "estimation-agent-quick-v3__timeline-label--with-icon"
      )}
    >
      {iconClass ?
        <i
          className={clsx(iconClass, "estimation-agent-quick-v3__timeline-row-icon")}
          aria-hidden
        />
      : null}
      {item.label}
    </span>
  )
}

/**
 * @typedef {object} AssistantActivityPanelProps
 * @property {StreamActivitySegment[]} segments
 * @property {boolean} [streaming] Último bloque de actividad del mensaje en curso
 * @property {string} [className]
 */

/**
 * @param {AssistantActivityPanelProps} props
 */
export default function AssistantActivityPanel({
  segments,
  streaming = false,
  className,
}) {
  const [activeIndex, setActiveIndex] = useState(
    /** @type {number | null} */ (null)
  )
  const header = useMemo(
    () => accordionHeaderLabel(segments, streaming),
    [segments, streaming]
  )
  const timelineValue = useMemo(
    () => buildTimelineItems(segments, streaming),
    [segments, streaming]
  )
  const headerIsReady = header === READY_LABEL

  const headerAwaitingUser = useMemo(() => {
    const active = segments.filter((s) => s.status === "active")
    const lastActive = active[active.length - 1]
    return (
      lastActive?.awaitingUserApproval === true ||
      lastActive?.awaitingAskQuestionsInput === true
    )
  }, [segments])

  const headerIconClass = headerIsReady ?
      "pi pi-check-circle"
    : headerAwaitingUser ?
      "pi pi-clock"
    : "pi pi-spinner pi-spin"

  const headerNode = (
    <span className="estimation-agent-quick-v3__accordion-header-text">
      <i
        className={clsx(
          headerIconClass,
          "estimation-agent-quick-v3__accordion-header-icon"
        )}
        aria-hidden
      />
      {header}
    </span>
  )

  return (
    <div className={clsx("estimation-agent-quick-v3__activity", className)}>
      <Accordion
        className="estimation-agent-quick-v3__activity-accordion"
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <AccordionTab header={headerNode}>
          <Timeline
            className="estimation-agent-quick-v3__activity-timeline"
            value={timelineValue}
            dataKey="id"
            marker={() => (
              <span className="estimation-agent-quick-v3__timeline-marker" />
            )}
            content={(item) => <TimelineRowContent item={item} />}
          />
        </AccordionTab>
      </Accordion>
    </div>
  )
}
